import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { authService } from './authService';

class PushNotificationService {
  constructor() {
    this.initialized = false;
    this.currentToken = null;
    this.pendingToken = null;
    this.registrationListener = null;
    this.fcmTokenListener = null;
  }

  /**
   * Initialize push notifications
   * @returns {Promise<{status: string, requiresSettings?: boolean}>}
   */
  async initialize(forceReinit = false) {
    console.log('[Push Notification] Initialize called, forceReinit:', forceReinit);
    console.log('[Push Notification] Currently initialized:', this.initialized);
    console.log('[Push Notification] Is native platform:', Capacitor.isNativePlatform());
    
    if (this.initialized && !forceReinit) {
      console.log('[Push Notification] Already initialized, skipping');
      return { status: 'already_initialized' };
    }

    // Check if running on a native platform
    if (!Capacitor.isNativePlatform()) {
      console.log('[Push Notification] Not a native platform, skipping');
      return { status: 'not_native' };
    }

    try {
      // FIRST: Check current permission status before requesting
      const currentStatus = await PushNotifications.checkPermissions();
      console.log('[Push Notification] Current permission status:', currentStatus);
      
      // If already denied, we can't request again - user must go to Settings
      if (currentStatus.receive === 'denied') {
        console.log('[Push Notification] Permission was previously denied. User must enable in Settings.');
        return { status: 'denied', requiresSettings: true };
      }
      
      // If already granted, just register
      if (currentStatus.receive === 'granted') {
        console.log('[Push Notification] Permission already granted, registering...');
        
        // Double-check listeners are set up before registering
        if (!this.registrationListener) {
          console.warn('[Push Notification] WARNING: No registration listener found! Listeners should be added before initialize().');
        }
        
        // Register with Apple / Google to receive push notifications
        console.log('[Push Notification] Calling PushNotifications.register()...');
        await PushNotifications.register();
        this.initialized = true;
        console.log('[Push Notification] Push notification registration successful - waiting for token...');
        
        // Give it a moment for the token to arrive (iOS sometimes has a small delay)
        setTimeout(() => {
          if (!this.currentToken && !this.pendingToken) {
            console.warn('[Push Notification] WARNING: No token received after 2 seconds.');
            this.retryRegistration();
          } else {
            console.log('[Push Notification] Token received successfully!');
          }
        }, 2000);
        
        return { status: 'granted' };
      }
      
      // If prompt, request permission
      console.log('[Push Notification] Requesting permissions...');
      const permissionStatus = await PushNotifications.requestPermissions();
      console.log('[Push Notification] Permission status:', permissionStatus);
      
      if (permissionStatus.receive === 'granted') {
        console.log('[Push Notification] Permission granted, registering...');
        
        // Double-check listeners are set up before registering
        if (!this.registrationListener) {
          console.warn('[Push Notification] WARNING: No registration listener found! Listeners should be added before initialize().');
        }
        
        // Register with Apple / Google to receive push notifications
        console.log('[Push Notification] Calling PushNotifications.register()...');
        await PushNotifications.register();
        this.initialized = true;
        console.log('[Push Notification] Push notification registration successful - waiting for token...');
        console.log('[Push Notification] Registration complete, token should arrive via listener');
        console.log('[Push Notification] Listeners should be set up. If token doesn\'t arrive, check listener setup.');
        
        // Give it a moment for the token to arrive (iOS sometimes has a small delay)
        setTimeout(() => {
          if (!this.currentToken && !this.pendingToken) {
            console.warn('[Push Notification] WARNING: No token received after 2 seconds.');
            console.warn('[Push Notification] This might mean:');
            console.warn('[Push Notification] 1. Listener wasn\'t set up before register() was called');
            console.warn('[Push Notification] 2. Token event isn\'t firing (iOS issue)');
            console.warn('[Push Notification] 3. Token was received but listener didn\'t fire');
            console.warn('[Push Notification] Current token:', this.currentToken);
            console.warn('[Push Notification] Pending token:', this.pendingToken);
            console.warn('[Push Notification] Registration listener exists:', !!this.registrationListener);
            
            // Try re-registering once more (iOS sometimes needs a retry)
            console.warn('[Push Notification] Attempting to re-register to trigger token...');
            this.retryRegistration();
          } else {
            console.log('[Push Notification] Token received successfully!');
          }
        }, 2000);
        
        return { status: 'granted' };
      } else {
        console.log('[Push Notification] Push notification permission denied:', permissionStatus);
        return { status: 'denied', requiresSettings: true };
      }
    } catch (error) {
      console.error('[Push Notification] Error initializing push notifications:', error);
      console.error('[Push Notification] Error stack:', error.stack);
      return { status: 'error', error: error.message };
    }
  }

  /**
   * Add listeners for push notification events
   * @param {Object} handlers - Object containing event handlers
   * @param {Function} handlers.onRegistration - Called when device is registered
   * @param {Function} handlers.onRegistrationError - Called when registration fails
   * @param {Function} handlers.onPushReceived - Called when notification is received
   * @param {Function} handlers.onPushActionPerformed - Called when notification is tapped
   */
  addListeners(handlers = {}) {
    console.log('[Push Notification] Adding listeners...');
    console.log('[Push Notification] Is native platform:', Capacitor.isNativePlatform());
    
    if (!Capacitor.isNativePlatform()) {
      console.log('[Push Notification] Not native platform, skipping listener setup');
      return;
    }

    console.log('[Push Notification] Setting up registration listener...');
    
    // Store handlers for later use
    this.registrationHandlers = handlers;
    
    // IMPORTANT: Don't remove all listeners here - we might remove other important listeners
    // Instead, just add our listeners (they won't duplicate if we're careful)
    
    // On registration success
    console.log('[Push Notification] Creating registration listener callback...');
    const registrationListener = PushNotifications.addListener('registration', (token) => {
      console.log('[Push Notification] ===== TOKEN RECEIVED =====');
      console.log('[Push Notification] Full token object:', JSON.stringify(token));
      console.log('[Push Notification] Token value:', token.value);
      console.log('[Push Notification] Token type:', typeof token.value);
      
      if (!token || !token.value) {
        console.error('[Push Notification] Token object is invalid:', token);
        return;
      }
      
      // Store the token
      this.currentToken = token.value;
      console.log('[Push Notification] Token stored in currentToken');
      
      if (handlers.onRegistration) {
        console.log('[Push Notification] Calling onRegistration handler...');
        handlers.onRegistration(token.value);
      }
      
      // Store the token in your backend
      console.log('[Push Notification] Calling saveTokenToServer with token:', token.value.substring(0, 20) + '...');
      this.saveTokenToServer(token.value);
    });
    
    // Store listener reference so we can remove it later if needed
    this.registrationListener = registrationListener;
    console.log('[Push Notification] Registration listener added successfully');
    console.log('[Push Notification] Listener reference stored:', !!this.registrationListener);

    // On registration error
    PushNotifications.addListener('registrationError', (error) => {
      console.error('[Push Notification] ===== REGISTRATION ERROR =====');
      console.error('[Push Notification] Error on registration:', error);
      console.error('[Push Notification] Error details:', JSON.stringify(error));
      if (handlers.onRegistrationError) {
        handlers.onRegistrationError(error);
      }
    });

    // On push notification received
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received:', notification);
      if (handlers.onPushReceived) {
        handlers.onPushReceived(notification);
      }
    });

    // On push notification action performed
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      console.log('Push notification action performed:', notification);
      if (handlers.onPushActionPerformed) {
        handlers.onPushActionPerformed(notification);
      }
    });
    
    // Listen for FCM token from native (workaround for Capacitor Push Notifications not firing)
    // This listens for the custom event sent from AppDelegate
    console.log('[Push Notification] Setting up FCM token listener via window events...');
    
    // Listen for FCM token via window events (fallback method)
    const handleFCMToken = (event) => {
      console.log('[Push Notification] ===== FCM TOKEN EVENT RECEIVED =====');
      console.log('[Push Notification] Event:', event);
      console.log('[Push Notification] Event detail:', event.detail);
      
      if (event.detail && event.detail.token) {
        const fcmToken = event.detail.token;
        console.log('[Push Notification] ===== FCM TOKEN RECEIVED (via window event) =====');
        console.log('[Push Notification] FCM Token:', fcmToken);
        console.log('[Push Notification] FCM Token length:', fcmToken.length);
        this.handleTokenReceived(fcmToken, handlers);
      } else {
        console.warn('[Push Notification] ⚠️ FCM token event received but no token in event.detail');
      }
    };
    
    window.addEventListener('fcmTokenReceived', handleFCMToken);
    this.fcmTokenListener = handleFCMToken;
    console.log('[Push Notification] ✅ FCM token event listener added');
    
    // Also expose the service to window for direct access from AppDelegate
    window.pushNotificationService = this;
    console.log('[Push Notification] ✅ pushNotificationService exposed to window');
    
    // Add method to request FCM token manually (for debugging)
    window.requestFCMToken = () => {
      console.log('[Push Notification] 🔍 Manually requesting FCM token from native...');
      // This will be handled by a Capacitor plugin or we can use a workaround
      // For now, we'll try to trigger it via a custom event that native can listen to
      window.dispatchEvent(new CustomEvent('requestFCMToken'));
      console.log('[Push Notification] ✅ Request event dispatched');
    };
    
    // Also try to get FCM token directly after a delay (in case event already fired)
    // Check immediately first, then again after a delay
    this.checkForFCMToken(handlers);
    setTimeout(() => {
      this.checkForFCMToken(handlers);
    }, 1000);
    // Check again after listeners are fully set up
    setTimeout(() => {
      this.checkForFCMToken(handlers);
    }, 3000);
  }
  
  /**
   * Handle token received (either from Capacitor Push Notifications or FCM)
   */
  handleTokenReceived(token, handlers) {
    if (!token) {
      console.warn('[Push Notification] ⚠️ handleTokenReceived called with null/undefined token');
      return;
    }
    
    console.log('[Push Notification] ===== HANDLING TOKEN RECEIVED =====');
    console.log('[Push Notification] Token length:', token.length);
    console.log('[Push Notification] Token preview:', token.substring(0, 20) + '...');
    
    // Store the token
    this.currentToken = token;
    console.log('[Push Notification] ✅ Token stored in currentToken');
    
    // Also store in localStorage for persistence
    try {
      localStorage.setItem('fcm_token', token);
      console.log('[Push Notification] ✅ Token stored in localStorage');
    } catch (error) {
      console.warn('[Push Notification] ⚠️ Could not store token in localStorage:', error);
    }
    
    if (handlers && handlers.onRegistration) {
      console.log('[Push Notification] Calling onRegistration handler...');
      handlers.onRegistration(token);
    }
    
    // Store the token in your backend
    console.log('[Push Notification] Calling saveTokenToServer with token:', token.substring(0, 20) + '...');
    this.saveTokenToServer(token).then(() => {
      console.log('[Push Notification] ✅ saveTokenToServer completed');
    }).catch((error) => {
      console.error('[Push Notification] ❌ saveTokenToServer failed:', error);
    });
  }
  
  /**
   * Check for FCM token (workaround for iOS issue)
   */
  async checkForFCMToken(handlers) {
    console.log('[Push Notification] 🔍 Checking for FCM token...');
    console.log('[Push Notification] Checking window.fcmToken:', !!window.fcmToken);
    console.log('[Push Notification] Checking localStorage:', !!localStorage.getItem('fcm_token'));
    console.log('[Push Notification] Checking currentToken:', !!this.currentToken);
    
    // Check if token is already stored in window (from AppDelegate injection)
    if (window.fcmToken) {
      console.log('[Push Notification] ✅ Found FCM token in window.fcmToken');
      console.log('[Push Notification] Token preview:', window.fcmToken.substring(0, 30) + '...');
      this.handleTokenReceived(window.fcmToken, handlers);
      return window.fcmToken;
    }
    
    // Check if token is in localStorage (from previous session)
    const storedToken = localStorage.getItem('fcm_token');
    if (storedToken) {
      console.log('[Push Notification] ✅ Found FCM token in localStorage');
      console.log('[Push Notification] Token preview:', storedToken.substring(0, 30) + '...');
      this.handleTokenReceived(storedToken, handlers);
      return storedToken;
    }
    
    // Check if we already have a currentToken
    if (this.currentToken) {
      console.log('[Push Notification] ✅ Found FCM token in currentToken');
      console.log('[Push Notification] Token preview:', this.currentToken.substring(0, 30) + '...');
      return this.currentToken;
    }
    
    // Try to get token from Firebase Messaging directly (if available)
    try {
      // This would require a custom Capacitor plugin, but we can check if Firebase is available
      if (window.Firebase && window.Firebase.messaging) {
        const token = await window.Firebase.messaging().getToken();
        if (token) {
          console.log('[Push Notification] ✅ Got FCM token directly from Firebase');
          this.handleTokenReceived(token, handlers);
          return token;
        }
      }
    } catch (error) {
      console.log('[Push Notification] Could not get token from Firebase directly:', error);
    }
    
    console.log('[Push Notification] ⚠️ No FCM token found yet, waiting for event...');
    return null;
  }

  /**
   * Remove all push notification listeners
   */
  removeAllListeners() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    PushNotifications.removeAllListeners();
  }

  /**
   * Get delivery notifications
   * @returns {Promise<Array>} - List of notifications
   */
  async getDeliveredNotifications() {
    if (!Capacitor.isNativePlatform()) {
      return [];
    }

    return await PushNotifications.getDeliveredNotifications();
  }

  /**
   * Save the device token to the server
   * @param {string} token - The device token
   */
  async saveTokenToServer(token) {
    // Declare variables at function scope so they're available in catch block
    let phoneNumber = null;
    let playerId = null;
    let userTypeString = 'player';
    let accessToken = null;
    
    try {
      console.log('[Push Notification] Attempting to save token to server:', token.substring(0, 20) + '...');
      
      // Get phone number from session storage - this is the primary identifier
      // Phone number should be available after login
      phoneNumber = sessionStorage.getItem('phone') || sessionStorage.getItem('user_phone');
      
      console.log('[Push Notification] Phone number from sessionStorage:', phoneNumber);
      console.log('[Push Notification] SessionStorage keys:', Object.keys(sessionStorage));
      
      if (!phoneNumber) {
        console.log('[Push Notification] Phone number not found in sessionStorage, checking login data...');
        // Try to get from the login response if available
        // Store token temporarily to register after login
        this.pendingToken = token;
        console.log('[Push Notification] Storing token as pending - will register after phone number is available');
        return;
      }
      
      // Optionally get player_id for backward compatibility (not required)
      playerId = sessionStorage.getItem('player_id') || sessionStorage.getItem('user_id');

      userTypeString = this.getUserTypeString();
      
      // Get user roles for logging
      const userRoles = [];
      const hasPlayerId = sessionStorage.getItem('player_id');
      if (hasPlayerId) {
        userRoles.push('player');
      }
      const isParent = sessionStorage.getItem('is_parent') === 'true' || 
                       sessionStorage.getItem('user_type') === 'parent';
      if (isParent) {
        userRoles.push('parent');
      }
      const isManager = sessionStorage.getItem('is_manager') === 'true';
      const userType = sessionStorage.getItem('user_type');
      const isCoach = userType === 'coach' || userType === 'manager';
      if (isManager || isCoach) {
        userRoles.push('coach');
      }
      const uniqueRoles = [...new Set(userRoles)].sort();
      
      console.log('[Push Notification] User Roles detected:', uniqueRoles);
      console.log('[Push Notification] User Type string:', userTypeString);

      // Get access token for authentication (optional since register_device.php is in skip auth list)
      accessToken = sessionStorage.getItem('access_token');
      
      // Prepare headers
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Add authorization header if token exists
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      // Send the token to your server with phone number as primary identifier
      const response = await fetch(`${process.env.REACT_APP_API_URL}/register_device.php`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          phone: phoneNumber, // Primary identifier
          player_id: playerId || null, // Optional, for backward compatibility
          user_type: userTypeString, // Comma-separated: 'player,parent,coach' or 'player,coach' etc.
          device_token: token,
          device_type: Capacitor.getPlatform() // 'ios' or 'android'
        })
      });

      console.log('[Push Notification] Server response status:', response.status);
      
      // Check if response is OK before trying to parse JSON
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Push Notification] Server returned error status:', response.status);
        console.error('[Push Notification] Error response body:', errorText);
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('[Push Notification] Server response:', result);
      
      if (result.success) {
        console.log('[Push Notification] ✅ Device token registered successfully via register_device.php');
        this.pendingToken = null; // Clear pending token
        
        // Also call check_device_token.php to ensure it's there (double-check)
        console.log('[Push Notification] Verifying token via check_device_token.php...');
        const phoneNumber = sessionStorage.getItem('phone') || sessionStorage.getItem('user_phone');
        if (phoneNumber) {
          try {
            const accessToken = sessionStorage.getItem('access_token') || authService.getAccessToken();
            const headers = { 'Content-Type': 'application/json' };
            if (accessToken) {
              headers['Authorization'] = `Bearer ${accessToken}`;
            }
            
            const checkResponse = await fetch(`${process.env.REACT_APP_API_URL}/check_device_token.php`, {
              method: 'POST',
              headers: headers,
              body: JSON.stringify({
                phone: phoneNumber,
                device_token: token,
                device_type: Capacitor.getPlatform(),
                user_type: this.getUserTypeString()
              })
            });
            
            if (checkResponse.ok) {
              const checkResult = await checkResponse.json();
              if (checkResult.success) {
                if (checkResult.inserted || checkResult.updated) {
                  console.log('[Push Notification] ✅ Token confirmed in database via check_device_token.php');
                } else if (checkResult.exists) {
                  console.log('[Push Notification] ✅ Token already exists in database');
                }
              }
            }
          } catch (error) {
            console.warn('[Push Notification] Could not verify via check_device_token.php:', error);
          }
        }
        
        // Verify token was actually saved to database
        setTimeout(async () => {
          const verification = await this.verifyTokenInDatabase();
          if (!verification.exists) {
            console.error('[Push Notification] ⚠️ WARNING: Token registration reported success but token not found in database!');
            console.error('[Push Notification] Attempting to save again...');
            await this.saveTokenToServer(token);
          } else {
            console.log('[Push Notification] ✅ Token verified in database');
          }
        }, 1000);
      } else {
        console.error('[Push Notification] ❌ Failed to register device token:', result.message);
        console.error('[Push Notification] Full response:', result);
      }
    } catch (error) {
      console.error('[Push Notification] ❌ Error saving token to server:', error);
      console.error('[Push Notification] Error details:', {
        message: error.message,
        stack: error.stack,
        url: `${process.env.REACT_APP_API_URL}/register_device.php`,
        playerId: playerId,
        userType: userTypeString,
        hasAccessToken: !!accessToken
      });
      
      // Store token as pending if phone number wasn't available
      if (!phoneNumber) {
        console.log('[Push Notification] Storing token as pending (phone number not available)');
        this.pendingToken = token;
      }
    }
  }

  /**
   * Register pending token if user is now logged in
   * Call this after successful login
   */
  async registerPendingToken() {
    if (this.pendingToken) {
      console.log('[Push Notification] Registering pending token after login');
      await this.saveTokenToServer(this.pendingToken);
    } else if (this.currentToken) {
      // If we have a current token but it wasn't saved (user wasn't logged in), try again
      console.log('[Push Notification] Re-registering current token after login');
      await this.saveTokenToServer(this.currentToken);
    } else {
      console.log('[Push Notification] No pending or current token to register');
    }
  }
  
  /**
   * Get the current token if available
   * @returns {string|null} The current device token
   */
  getCurrentToken() {
    return this.currentToken || this.pendingToken || window.fcmToken || localStorage.getItem('fcm_token') || null;
  }
  
  /**
   * Get user type string for API calls
   * @returns {string} Comma-separated user roles (e.g., 'player,coach')
   */
  getUserTypeString() {
    const userRoles = [];
    
    // Check if user is a player
    const hasPlayerId = sessionStorage.getItem('player_id');
    if (hasPlayerId) {
      userRoles.push('player');
    }
    
    // Check if user is a parent
    const isParent = sessionStorage.getItem('is_parent') === 'true' || 
                     sessionStorage.getItem('user_type') === 'parent';
    if (isParent) {
      userRoles.push('parent');
    }
    
    // Check if user is a coach/manager
    const isManager = sessionStorage.getItem('is_manager') === 'true';
    const userType = sessionStorage.getItem('user_type');
    const isCoach = userType === 'coach' || userType === 'manager';
    if (isManager || isCoach) {
      userRoles.push('coach');
    }
    
    // Remove duplicates and sort for consistency
    const uniqueRoles = [...new Set(userRoles)].sort();
    
    // Default to 'player' if no roles found
    return uniqueRoles.length > 0 ? uniqueRoles.join(',') : 'player';
  }
  
  /**
   * Check if notification permissions are granted
   * @returns {Promise<boolean>}
   */
  async checkPermissionStatus() {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      const permissionStatus = await PushNotifications.checkPermissions();
      return permissionStatus.receive === 'granted';
    } catch (error) {
      console.error('[Push Notification] Error checking permission status:', error);
      return false;
    }
  }

  /**
   * @returns {Promise<'granted'|'denied'|'prompt'|'not_native'|'error'>}
   */
  async getReceiveStatus() {
    if (!Capacitor.isNativePlatform()) {
      return 'not_native';
    }

    try {
      const permissionStatus = await PushNotifications.checkPermissions();
      return permissionStatus.receive || 'prompt';
    } catch (error) {
      console.error('[Push Notification] Error reading permission status:', error);
      return 'error';
    }
  }

  async openSystemNotificationSettings() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      const { App } = await import('@capacitor/app');
      const platform = Capacitor.getPlatform();

      if (platform === 'ios') {
        await App.openUrl({ url: 'app-settings:' });
        return;
      }

      try {
        await App.openUrl({ url: 'package:app.firstwhistle.my' });
      } catch (e) {
        window.alert('Please go to Settings → Apps → FirstWhistle → Notifications and enable notifications');
      }
    } catch (error) {
      console.error('[Push Notification] Error opening Settings:', error);
      const platform = Capacitor.getPlatform();
      if (platform === 'ios') {
        window.alert('Please go to Settings → FirstWhistle → Notifications and turn on Allow Notifications');
      } else {
        window.alert('Please go to Settings → Apps → FirstWhistle → Notifications and enable notifications');
      }
    }
  }

  /**
   * If the user has turned notifications on in Settings, register and save the token.
   * @returns {Promise<boolean>}
   */
  async recoverIfGranted() {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    const granted = await this.checkPermissionStatus();
    if (!granted) {
      return false;
    }

    if (this.initialized && this.getCurrentToken()) {
      window.dispatchEvent(new CustomEvent('fw:push-permission-granted'));
      return true;
    }

    const result = await this.initialize(true);
    const recovered = !result || result.status === 'granted' || result.status === 'already_initialized';
    if (recovered) {
      window.dispatchEvent(new CustomEvent('fw:push-permission-granted'));
    }
    return recovered;
  }

  /**
   * Verify that the device token exists in the database for the current user
   * @returns {Promise<{exists: boolean, token?: string}>}
   */
  async verifyTokenInDatabase() {
    try {
      const phoneNumber = sessionStorage.getItem('phone') || sessionStorage.getItem('user_phone');
      
      if (!phoneNumber) {
        console.log('[Push Notification] Cannot verify token - phone number not available');
        return { exists: false, reason: 'no_phone' };
      }

      const accessToken = sessionStorage.getItem('access_token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/check_device_token.php`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          phone: phoneNumber
        })
      });

      if (!response.ok) {
        console.error('[Push Notification] Error checking device token:', response.status);
        return { exists: false, reason: 'api_error' };
      }

      const result = await response.json();
      
      if (result.success && result.exists) {
        console.log('[Push Notification] ✅ Device token verified in database');
        return { exists: true, token: result.device_token };
      } else {
        console.warn('[Push Notification] ⚠️ Device token NOT found in database');
        return { exists: false, reason: 'not_found' };
      }
    } catch (error) {
      console.error('[Push Notification] Error verifying token in database:', error);
      return { exists: false, reason: 'error', error: error.message };
    }
  }

  /**
   * Verify and retry saving token if it's missing from database
   * @returns {Promise<boolean>} True if token exists or was successfully saved
   */
  async verifyAndRetryToken() {
    const verification = await this.verifyTokenInDatabase();
    
    if (verification.exists) {
      return true;
    }

    // Token is missing - try to save it
    console.log('[Push Notification] Token missing from database, attempting to save...');
    
    const token = this.currentToken || this.pendingToken;
    if (!token) {
      console.warn('[Push Notification] No token available to save');
      return false;
    }

    try {
      await this.saveTokenToServer(token);
      
      // Wait a moment and verify again
      await new Promise(resolve => setTimeout(resolve, 1000));
      const recheck = await this.verifyTokenInDatabase();
      
      if (recheck.exists) {
        console.log('[Push Notification] ✅ Token successfully saved and verified');
        return true;
      } else {
        console.error('[Push Notification] ❌ Token save failed or verification failed');
        return false;
      }
    } catch (error) {
      console.error('[Push Notification] Error retrying token save:', error);
      return false;
    }
  }

  /**
   * Retry registration - sometimes iOS needs a second attempt
   */
  async retryRegistration() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    
    try {
      console.log('[Push Notification] Retrying registration...');
      // Try registering again - sometimes this triggers the token event
      await PushNotifications.register();
      console.log('[Push Notification] Re-registration complete, waiting for token...');
      
      // Also check for stored token
      setTimeout(async () => {
        // Check for token in various places
        if (window.fcmToken) {
          console.log('[Push Notification] ✅ Found token in window.fcmToken after retry');
          this.handleTokenReceived(window.fcmToken);
          return;
        }
        
        const storedToken = localStorage.getItem('fcm_token');
        if (storedToken) {
          console.log('[Push Notification] ✅ Found token in localStorage after retry');
          this.handleTokenReceived(storedToken);
          return;
        }
        
        if (!this.currentToken && !this.pendingToken) {
          console.error('[Push Notification] ERROR: Token still not received after retry.');
          console.error('[Push Notification] This might indicate an APNs configuration issue.');
          console.error('[Push Notification] Check:');
          console.error('[Push Notification] 1. APNs key is uploaded to Firebase');
          console.error('[Push Notification] 2. Bundle ID matches in Firebase and Xcode');
          console.error('[Push Notification] 3. Push Notifications capability is enabled');
          console.error('[Push Notification] 4. App is running on a physical device (not simulator)');
          console.error('[Push Notification] 5. Check Xcode console for [AppDelegate] logs');
          console.error('[Push Notification] 6. Verify Firebase is configured correctly');
        } else {
          console.log('[Push Notification] ✅ Token received after retry!');
        }
      }, 3000);
    } catch (error) {
      console.error('[Push Notification] Error during retry registration:', error);
    }
  }
  
  /**
   * Manually check for FCM token (for debugging)
   */
  async manuallyCheckForToken() {
    console.log('[Push Notification] 🔍 Manually checking for FCM token...');
    
    // Check window.fcmToken
    if (window.fcmToken) {
      console.log('[Push Notification] ✅ Found token in window.fcmToken:', window.fcmToken.substring(0, 30) + '...');
      this.handleTokenReceived(window.fcmToken);
      return window.fcmToken;
    }
    
    // Check localStorage
    const storedToken = localStorage.getItem('fcm_token');
    if (storedToken) {
      console.log('[Push Notification] ✅ Found token in localStorage:', storedToken.substring(0, 30) + '...');
      this.handleTokenReceived(storedToken);
      return storedToken;
    }
    
    // Check currentToken
    if (this.currentToken) {
      console.log('[Push Notification] ✅ Found token in currentToken:', this.currentToken.substring(0, 30) + '...');
      return this.currentToken;
    }
    
    // Check pendingToken
    if (this.pendingToken) {
      console.log('[Push Notification] ✅ Found token in pendingToken:', this.pendingToken.substring(0, 30) + '...');
      return this.pendingToken;
    }
    
    console.warn('[Push Notification] ⚠️ No token found in any location');
    return null;
  }
  
  /**
   * Manually set a token for testing (use the token from Xcode console)
   * Usage: pushNotificationService.setTestToken('your-token-here')
   */
  setTestToken(token) {
    if (!token || typeof token !== 'string') {
      console.error('[Push Notification] ❌ Invalid token provided');
      return false;
    }
    
    console.log('[Push Notification] 🔧 Manually setting test token:', token.substring(0, 30) + '...');
    
    // Store in all locations
    this.currentToken = token;
    window.fcmToken = token;
    localStorage.setItem('fcm_token', token);
    
    // Handle it as if it was received
    this.handleTokenReceived(token);
    
    console.log('[Push Notification] ✅ Test token set and processed');
    return true;
  }
}

export const pushNotificationService = new PushNotificationService(); 